/* Fundle logo + brand logo helpers — white-theme edition */
import React, { useState } from "react";

const FUNDLE_LOGO_WHITE =
    "https://customer-assets.emergentagent.com/job_silly-knuth-6/artifacts/4uqlwpaa_erj2rwul_fundle_logo-white-min-382x100%20%282%29.png";

/**
 * Fundle Logo — uses the official white-on-dark wordmark.
 * On light backgrounds we wrap it in a navy pill so the white logo stays legible.
 * Pass `dark` when the surrounding surface is already dark (footer, hero banners).
 */
export function FundleLogo({ className = "h-9 w-auto", dark = false, bare = false }) {
    const img = (
        <img
            src={FUNDLE_LOGO_WHITE}
            alt="Fundle Experiences"
            className="h-full w-auto object-contain"
            data-testid="fundle-logo"
        />
    );
    if (dark || bare) {
        return <div className={`flex items-center ${className}`}>{img}</div>;
    }
    return (
        <div
            className={`inline-flex items-center bg-[#0B1437] rounded-xl px-3 py-1.5 shadow-[0_4px_12px_-4px_rgba(11,20,55,0.35)] ${className}`}
        >
            {img}
        </div>
    );
}

/** A subtle Fundle wordmark fallback (used in footer/admin if image fails). */
export function FundleWordmark({ className = "" }) {
    return (
        <span className={`font-display font-extrabold text-xl tracking-tight text-[#0b1437] ${className}`}>
            Fundle<span className="text-[#c9a959]">.</span>experiences
        </span>
    );
}

const PALETTE = [
    ["#0b1437", "#1a2455"],
    ["#c9a959", "#a07e2c"],
    ["#e11d48", "#9f1239"],
    ["#16a34a", "#166534"],
    ["#7c3aed", "#5b21b6"],
    ["#0ea5e9", "#075985"],
    ["#f97316", "#c2410c"],
    ["#db2777", "#9d174d"],
    ["#0891b2", "#155e75"],
    ["#65a30d", "#3f6212"],
];

function pickColor(name = "") {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return PALETTE[h % PALETTE.length];
}

function initialsOf(name) {
    return (name || "?")
        .replace(/[^A-Za-z0-9 ]/g, "")
        .split(" ")
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

/** Image with smart fallback: if `onLoad` reports a natural width below MIN_PX
 *  (i.e. Google returned a 16×16 placeholder favicon), advance to next stage. */
function SmartImg({ src, alt, onAdvance, minPx = 48, ...rest }) {
    const handleLoad = (e) => {
        const w = e.target.naturalWidth || 0;
        const h = e.target.naturalHeight || 0;
        if (w > 0 && w < minPx && h < minPx) {
            // tiny placeholder — treat as failure
            onAdvance();
        }
    };
    return (
        <img
            src={src}
            alt={alt}
            referrerPolicy="no-referrer"
            onError={onAdvance}
            onLoad={handleLoad}
            {...rest}
        />
    );
}

/** Small inline brand mark (avatar size). Tries Clearbit, then Google favicon, then monogram. */
export function BrandLogo({ url, fallback, name, size = 56, className = "" }) {
    const [stage, setStage] = useState(0); // 0 primary, 1 fallback, 2 monogram
    const src = stage === 0 ? url : stage === 1 ? fallback : null;

    if (!src) {
        const [c1, c2] = pickColor(name);
        return (
            <div
                style={{ width: size, height: size, background: `linear-gradient(135deg, ${c1}, ${c2})`, fontSize: Math.max(11, size * 0.32) }}
                className={`rounded-xl flex items-center justify-center text-white font-display font-extrabold tracking-tight ${className}`}
                data-testid={`brand-logo-fallback-${name}`}
                aria-label={name}
            >
                {initialsOf(name) || "FX"}
            </div>
        );
    }
    return (
        <div
            style={{ width: size, height: size }}
            className={`rounded-xl bg-white p-1.5 flex items-center justify-center overflow-hidden border border-[rgba(11,20,55,0.06)] ${className}`}
        >
            <SmartImg
                src={src}
                alt={name}
                minPx={32}
                onAdvance={() => setStage((s) => s + 1)}
                className="max-w-full max-h-full object-contain"
                data-testid={`brand-logo-${name}`}
            />
        </div>
    );
}

/** Big tile that fills its container — used on voucher / brand cards. Tries 3 stages. */
export function BrandTile({ url, fallback, name, className = "" }) {
    const [stage, setStage] = useState(0);
    const src = stage === 0 ? url : stage === 1 ? fallback : null;

    if (!src) {
        const [c1, c2] = pickColor(name);
        return (
            <div
                style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                className={`w-full h-full flex flex-col items-center justify-center text-white p-4 ${className}`}
                data-testid={`brand-tile-fallback-${name}`}
                aria-label={name}
            >
                <span className="font-display font-extrabold text-5xl tracking-tight drop-shadow-md">
                    {initialsOf(name)}
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] mt-2 opacity-85 line-clamp-1">{name}</span>
            </div>
        );
    }
    return (
        <div className={`w-full h-full bg-white flex items-center justify-center p-5 ${className}`}>
            <SmartImg
                src={src}
                alt={name}
                minPx={48}
                onAdvance={() => setStage((s) => s + 1)}
                className="max-w-full max-h-full object-contain"
                data-testid={`brand-tile-${name}`}
            />
        </div>
    );
}
